import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Typography,
  TableSortLabel,
} from "@mui/material";

const DataTable = ({
  columns,
  data = [],
  loading = false,
  sortConfig = { field: null, direction: null },
  onSort,
  keyField = "id",
  responsive = false,
  tableLayout = "auto",
}) => {
  // Sort direction toggle handler
  const handleSortClick = (field) => {
    if (!onSort) return;

    let direction = "asc";

    // If already sorted by this field, toggle direction
    if (sortConfig.field === field) {
      direction = sortConfig.direction === "asc" ? "desc" : "asc";
    }

    onSort(field, direction);
  };

  const getCellSx = (column, zIndex) => {
    if (!responsive && !column.compact && !column.cellSx) {
      return undefined;
    }

    return {
      ...(responsive
        ? {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }
        : {}),
      ...(column.compact ? { px: 0.5 } : {}),
      ...(column.sticky === "right"
        ? {
            position: "sticky",
            right: 0,
            zIndex,
            backgroundColor: "background.paper",
            boxShadow: "-4px 0 8px rgba(0, 0, 0, 0.08)",
          }
        : {}),
      ...(column.cellSx || {}),
    };
  };

  return (
    <TableContainer
      component={Paper}
      sx={responsive ? { overflowX: "visible" } : undefined}
    >
      <Table
        sx={
          responsive
            ? { minWidth: "100%", tableLayout: "fixed" }
            : { tableLayout }
        }
      >
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.field}
                align={column.align || "left"}
                style={{
                  width: column.width,
                  minWidth: column.minWidth,
                  maxWidth: column.maxWidth,
                }}
                sx={getCellSx(column, 3)}
              >
                {column.sortable ? (
                  <TableSortLabel
                    active={sortConfig.field === column.field}
                    direction={
                      sortConfig.field === column.field
                        ? sortConfig.direction
                        : "asc"
                    }
                    onClick={() => handleSortClick(column.field)}
                  >
                    {column.label}
                  </TableSortLabel>
                ) : (
                  column.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center">
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress />
                </Box>
              </TableCell>
            </TableRow>
          ) : data.length > 0 ? (
            data.map((row, index) => (
              <TableRow key={row[keyField] || index}>
                {columns.map((column) => (
                  <TableCell
                    key={column.field}
                    align={column.align || "left"}
                    sx={getCellSx(column, 2)}
                  >
                    {column.render
                      ? column.render(row, index)
                      : row[column.field]}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} align="center">
                <Typography variant="body1" color="text.secondary">
                  No records found
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DataTable;
